import {defineStore} from 'pinia'
import axios from '../http/axios';
import appMainAxios from '../http/appMainAxios';
import router from "../router";
import {parseQuery} from "../utils/url";

// Token Refresh
let isAlreadyFetchingAccessToken = false;
let subscribers = [];

const onAccessTokenFetched = (access_token) => {
    subscribers = subscribers.filter(callback => callback(access_token));
}

const addSubscriber = (callback) => {
    subscribers.push(callback);
}

export const useAuthStore = defineStore( 'authStore', {
    state: () => ({
        auth: {
            login_url: '/',
            api_login_url: '',
            api_refresh_url: '',
            api_logout_url: '',
        },
        user: {},
        permissions: [],
        isAdmin: false,
    }),
    getters: {
        showConfirmModal: (getters) => {
            return !getters.isEmailConfirmed;
        },
        isUserLoggedIn: () => {
            return localStorage.getItem('userInfo');
        },
        /**
         * @param state
         * @returns {(function(*): boolean)|*}
         * @example useAuthStore().can('permission_name');
         */
        can: (state) => {
            return (permission) => {
                if (state.isAdmin) {
                    return true;
                }
                return state.permissions.indexOf(permission) > -1;
            }
        },
        isEmailConfirmed: (state) => {
            if (state.user.userType !== 3) {
                return true;
            }
            let now = new Date();
            let expDate = localStorage.getItem('emailConfirmedTill');
            return (expDate !== null && now.getTime() < new Date(expDate).getTime())
        },
    },
    actions: {
        validateCode(code) {
            return new Promise((resolve, reject) => {
                appMainAxios.validateConfirmCode(code, this.user.id)
                    .then(response => {
                        if (!response.data.success) {
                            reject({message: response.data.message});
                        }
                        if (response.data.data.success == true) {
                            localStorage.setItem('emailConfirmedTill', response.data.data.expiredAt);
                            window.location.reload();
                            resolve(response);
                        }
                        reject({message: 'Invalid confirmation code'});
                    }).catch(error => {
                    reject(error.response.data);
                });
            })
        },
        sendConfirmationCode(resend = false) {
            return new Promise((resolve, reject) => {
                if (localStorage.getItem('confirmationEmailSent') !== 'true' || resend) {
                    appMainAxios.sendConfirmationLetter(this.user.id, resend)
                        .then(response => {
                            if (!response.data.success) {
                                reject({message: response.data.message});
                            }
                            localStorage.setItem('confirmationEmailSent', 'true');
                            resolve(response);
                        }).catch(error => {
                        reject(error);
                    });
                }
            });
        },
        loadAppAuthInfo () {
            return new Promise((resolve, reject) => {
                appMainAxios.info().then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    this.updateAppAuthInfo(response.data.data);

                    resolve(response);
                }).catch(error => {
                    reject(error);
                });
            });
        },
        updateAppAuthInfo(payload) {
            let appInfo = JSON.parse(localStorage.getItem('appInfo')) || this.auth;

            for (const property of Object.keys(payload)) {

                if (payload[property] != null) {
                    this.auth[property] = payload[property];
                    appInfo[property] = payload[property];
                }
            }

            localStorage.setItem('appInfo', JSON.stringify(appInfo));
        },
        loadUserInfo() {
            return new Promise((resolve, reject) => {
                appMainAxios.userInfo().then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    this.updateUserInfo(response.data.data.user, response.data.data.permissions, response.data.data.is_admin);

                    resolve(response);
                }).catch(error => {
                    reject(error);
                });
            });
        },
        updateUserInfo(userPayload, permissionsPayload, isAdmin = false) {
            let userInfo = JSON.parse(localStorage.getItem('userInfo')) || this.user;

            for (const property of Object.keys(userPayload)) {

                if (userPayload[property] != null) {
                    this.user[property] = userPayload[property];
                    userInfo[property] = userPayload[property];
                }

            }
            localStorage.setItem('userInfo', JSON.stringify(userInfo));

            this.isAdmin = isAdmin;
            this.permissions = permissionsPayload;
        },
        setAxiosBearerToken (accessToken) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
        },
        async init () {
            this.setAxiosBearerToken(localStorage.getItem('accessToken'));

            axios.interceptors.response.use(
                (response) => response,
                (error) => {
                    const { config, response, } = error;
                    const originalRequest = config;

                    if (response && response.status === 401) {
                        if (!isAlreadyFetchingAccessToken) {
                            isAlreadyFetchingAccessToken = true;
                            this.fetchAccessToken(localStorage.getItem('refreshToken'))
                                .then((response) => {
                                    isAlreadyFetchingAccessToken = false;

                                    onAccessTokenFetched(response.data.data.access_token);
                                })
                                .catch(() => {
                                    localStorage.setItem('returnUrl', window.location.pathname);
                                    window.location.href = this.auth.login_url;
                                });
                        }

                        return new Promise((resolve) => {
                            addSubscriber(access_token => {
                                originalRequest.headers.Authorization = 'Bearer ' + access_token;

                                resolve(axios(originalRequest));
                            });
                        });
                    }
                    return Promise.reject(error);
                });

            await this.loadAppAuthInfo();

            let query = parseQuery(window.location.hash || '');
            if (localStorage.getItem('accessToken') === null && query.hasOwnProperty('external_token') && query.external_token.length) {
                localStorage.setItem('accessToken', query.external_token);
                this.setAxiosBearerToken(query.external_token);
            }

            if (window.location.href.indexOf('/azurelogin/callback') === -1) {
                await this.loadUserInfo();
            }
        },
        fetchAccessToken (refreshToken) {
            return new Promise((resolve, reject) => {
                this.refreshToken(refreshToken).then(response => {
                    // If there's user data in response
                    if (response.data.data.user) {
                        localStorage.setItem('accessToken', response.data.data.access_token);
                        localStorage.setItem('refreshToken', response.data.data.refresh_token);

                        this.updateUserInfo(response.data.data.user, response.data.data.permissions, response.data.data.is_admin);
                        this.setAxiosBearerToken(response.data.data.access_token);

                        resolve(response);
                    } else {
                        reject({ message: 'Failed to refresh token' });
                    }
                }).catch(error => {
                    reject(error);
                });
            });
        },
        async loginWithAzureToken (token) {
            let response = await axios.post(this.auth.api_login_url, { token: token })
            // If there's user data in response
            if (response.data.data.user) {
                // Set accessToken
                localStorage.setItem('accessToken', response.data.data.access_token);
                localStorage.setItem('refreshToken', response.data.data.refresh_token);

                this.updateUserInfo(response.data.data.user, response.data.data.permissions, response.data.data.is_admin);
                this.setAxiosBearerToken(response.data.data.access_token);

                router.push(router.currentRoute?.query?.to || localStorage.getItem('returnUrl') || '/');
                localStorage.removeItem('returnUrl');
            } else {
                throw new Error('Cannot login');
            }
        },
        refreshToken (token) {
            return axios.post(this.auth.api_refresh_url, { token: token });
        },
        async logout()
        {
            if (localStorage.getItem("accessToken")) {
                localStorage.removeItem("accessToken");
            }

            let refreshToken = localStorage.getItem("refreshToken");

            if (refreshToken) {
                this.logoutWithToken(refreshToken);
                localStorage.removeItem("refreshToken");
            }

            localStorage.removeItem('userInfo');

            if (this.auth.login_url.indexOf('http') !== -1) {
                window.location = this.auth.login_url;
                return;
            }

            router.push(this.auth.login_url).catch(() => {});
        },
        logoutWithToken (token) {
            return axios.post(this.auth.api_logout_url, { token: token });
        },
    }
})
